import { SENTENCE_AUDIO_BYTES } from '../../../src/speech/tts/read-aloud-policy.js';

// A cold company dictionary may take 35 s; both provider attempts then have
// their own 15 s startup bound. Allow the server to return its actual outcome.
const HEADER_DEADLINE_MS = 75_000;
const CHUNK_DEADLINE_MS = 20_000;

export async function fetchAudio(
  fetchImpl: typeof fetch,
  body: string,
  parent: AbortSignal,
  maxBytes = SENTENCE_AUDIO_BYTES,
) {
  const deadline = new AbortController();
  const signal = AbortSignal.any([parent, deadline.signal]);
  const timer = setTimeout(
    () => deadline.abort(new Error('tts_request_timeout')),
    HEADER_DEADLINE_MS,
  );
  try {
    const response = await fetchImpl('/api/v1/speech/speak', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      signal,
    });
    signal.throwIfAborted();
    return {
      response,
      chunks: audioChunks(response.body, signal, deadline, maxBytes),
      cancel: () => deadline.abort(),
    };
  } finally {
    // The fetch signal still follows parent cancellation for the entire body.
    // Only the header deadline ends here; playback backpressure is not a timeout.
    clearTimeout(timer);
  }
}

async function readChunk(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
  deadline: AbortController,
) {
  signal.throwIfAborted();
  let rejectAbort!: (reason: unknown) => void;
  const aborted = new Promise<never>((_, reject) => {
    rejectAbort = reject;
  });
  const onAbort = () => rejectAbort(signal.reason);
  signal.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(
    () => deadline.abort(new Error('tts_stream_timeout')),
    CHUNK_DEADLINE_MS,
  );
  try {
    const next = await Promise.race([reader.read(), aborted]);
    signal.throwIfAborted();
    return next;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', onAbort);
  }
}

async function* audioChunks(
  body: ReadableStream<Uint8Array> | null,
  signal: AbortSignal,
  deadline: AbortController,
  maxBytes: number,
) {
  if (!body) throw new Error('tts_audio_empty');
  const reader = body.getReader();
  let bytes = 0;
  try {
    for (;;) {
      const next = await readChunk(reader, signal, deadline);
      if (next.done) return;
      bytes += next.value.byteLength;
      if (bytes > maxBytes) throw new Error('tts_audio_too_large');
      if (next.value.byteLength) yield next.value;
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

export async function collectAudio(chunks: AsyncIterable<Uint8Array>) {
  const parts = [];
  let length = 0;
  for await (const part of chunks) {
    parts.push(part);
    length += part.byteLength;
  }
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    bytes.set(part, offset);
    offset += part.byteLength;
  }
  return bytes.buffer;
}
